import { Client as ConversationsClient } from '@twilio/conversations';
import { useEffect, useCallback } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import { TwilioConnectionStatuses } from '../../constants/TwilioConnectionStatuses';
import { useLogoutEffect } from './useLogoutEffect';

// Not fucking around with state, this needs to be a singleton so like a highlander there can be only one!
let conversationsClient = null;

/**
 * @typedef {Object} UseManageTwilioConnectionHookProps
 * @property {String} token - The Twilio API token needed to sign requests to the Twilio API made from the client.
 * @property {Function} refreshToken - An async function to refresh the signed Twilio API token.
 */

/**
 * @typedef {Object} UseManageTwilioConnectionHookValue
 * @property {import('@twilio/conversations').Client} conversationsClient - the Twilio Conversations client singleton
 * @property {TwilioConnectionStatuses} connectionStatus - the current connection status of the Twilio Conversations client
 */

/**
 * A hook that manages the singleton Twilio connection.
 * @returns {UseManageTwilioConnectionHookValue}
 */
const useManageTwilioConnection = ({ token, refreshToken }) => {
  /**
   * @type {[
   *  TwilioConnectionStatuses,
   *  import('react').Dispatch<
   *    import('react').SetStateAction<
   *      TwilioConnectionStatuses
   *    >
   *  >
   * ]}
   */
  const [connectionStatus, setConnectionStatus] = useStateIfMounted(
    TwilioConnectionStatuses.UNINITIALIZED
  );

  /**
   * Register event handlers for the Twilio Conversations client.
   */
  const initConversationsClient = useCallback(async () => {
    setConnectionStatus(TwilioConnectionStatuses.INITIALIZING);

    const handleTokenRefresh = async userId => {
      const refreshedToken = await refreshToken(userId);
      // If we don't have a token, something has gone wrong. Let's reload the page.
      if (!refreshedToken) {
        window.location.reload();
      }
      if (conversationsClient)
        return conversationsClient?.updateToken(refreshedToken);

      configureClient(refreshedToken);
    };

    window.addEventListener('focus', handleTokenRefresh);

    const configureClient = async token => {
      const client = new ConversationsClient(token);
      conversationsClient = client;

      client.on('connectionStateChanged', state => setConnectionStatus(state));
      client.on('tokenAboutToExpire', handleTokenRefresh);
      client.on('tokenExpired', handleTokenRefresh);
    };

    configureClient(token);
  }, [token, conversationsClient, setConnectionStatus]);

  /**
   * Initialize the Twilio client when we have a token;
   * don't bother if we don't have a token, or the client
   * has already been initialized.
   */
  useEffect(() => {
    if (!token || connectionStatus !== TwilioConnectionStatuses.UNINITIALIZED)
      return;

    initConversationsClient();
  }, [token, connectionStatus, conversationsClient]);

  useLogoutEffect({
    conversationsClient,
    connectionStatus,
    setConnectionStatus,
    token,
  });

  return {
    conversationsClient,
    connectionStatus,
  };
};

export { useManageTwilioConnection };
